import { t } from "@lingui/macro"
import { makeAutoObservable } from "mobx"
import React from "react"

import { AsyncRequestService } from "src/api"
import { adminv3_AsyncRequestItemResponse } from "src/api/models/adminv3_AsyncRequestItemResponse"
import { avy_api_pkg_async_AsyncStatus } from "src/api/models/avy_api_pkg_async_AsyncStatus"
import { Channel } from "src/channel"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import {
    getOperatorForField,
    handleDateFilter,
    parseFieldValue,
} from "src/helpers/filterHelpers"
import {
    IPostInitialStateFilterItem,
    IRouteParams,
} from "src/views/community-posts-and-comments/types"
import { parseDate } from "src/lib/date"
import { createLoadingKeys } from "src/lib/loading"
import { Pagination } from "src/lib/pagination"
import { IAdvanceQueryModel, IColumn } from "src/types/data-grid-pro"

export interface IAsyncRequestItemListItem {
    id: number
    async_request_id: number
    status: avy_api_pkg_async_AsyncStatus
    attempts: number
    max_attempts: number
    created_at?: Date | null
    processed_at?: Date | null
    sent_to_pubsub_at?: Date | null
    error_code?: string
    error_message?: string
    worker_id?: string
    pubsub_message_id?: string
    payload_hash?: string
    payload?: string
}

export class AsyncRequestItemsStore implements IDisposable {
    static Context = React.createContext<AsyncRequestItemsStore | null>(null)
    static LoadingKeys = createLoadingKeys("pageLoad")

    _accessGroupId: number = DEFAULT_ACCESS_GROUP.id

    private repositoryUpdatesListenerDisposer?: () => void

    asyncRequestItems = new Pagination(
        async (params) => {
            const response =
                await AsyncRequestService.postV3AdminAsyncRequestItemList({
                    request: {
                        page_number: params.page,
                        page_size: params.pageSize,
                        filter: params.advanceQuery,
                    },
                })

            const items: IAsyncRequestItemListItem[] =
                response.items?.map(
                    (asyncRequestItem: adminv3_AsyncRequestItemResponse) => ({
                        id: asyncRequestItem.id ?? -1,
                        async_request_id:
                            asyncRequestItem.async_request_id ?? -1,
                        status: asyncRequestItem.status ?? "created",
                        attempts: asyncRequestItem.attempts ?? 0,
                        max_attempts: asyncRequestItem.max_attempts ?? 1,
                        created_at: parseDate(asyncRequestItem.created_at),
                        processed_at: parseDate(asyncRequestItem.processed_at),
                        sent_to_pubsub_at: parseDate(
                            asyncRequestItem.sent_to_pubsub_at,
                        ),
                        error_code: asyncRequestItem.error_code,
                        error_message: asyncRequestItem.error_message,
                        worker_id: asyncRequestItem.worker_id,
                        pubsub_message_id: asyncRequestItem.pubsub_message_id,
                        payload_hash: asyncRequestItem.payload_hash,
                    }),
                ) ?? []

            return {
                items,
                rawItems: response.items ?? [],
                count: response.total_count ?? 0,
            }
        },
        { loadingKey: AsyncRequestItemsStore.LoadingKeys.pageLoad },
    )

    constructor() {
        makeAutoObservable(this)
    }

    async init(accessGroupId: number, advanceQuery?: IAdvanceQueryModel) {
        this.setAccessGroupId(accessGroupId)

        // Listen to repository updates for real-time updates
        this.repositoryUpdatesListenerDisposer = Channel.addListener(
            async (event) => {
                if (
                    event.name === "repository/updated" &&
                    event.payload.repository === "async-request"
                ) {
                    await this.asyncRequestItems.reload()
                }
            },
        )

        await this.asyncRequestItems.loadInitialPage(advanceQuery)
    }

    getStatusChipState(
        status: avy_api_pkg_async_AsyncStatus,
    ): "positive" | "neutral" | "negative" | "info" | "other" | "dark" {
        switch (status) {
            case "completed":
                return "positive"
            case "failed":
                return "negative"
            case "processing":
                return "neutral"
            case "partial":
                return "neutral"
            case "pending":
            case "pending_send":
            case "sent":
                return "info"
            default:
                return "other"
        }
    }

    getStatusColor(
        status: avy_api_pkg_async_AsyncStatus,
    ):
        | "default"
        | "primary"
        | "secondary"
        | "error"
        | "info"
        | "success"
        | "warning" {
        switch (status) {
            case "completed":
                return "success"
            case "failed":
                return "error"
            case "processing":
                return "warning"
            case "partial":
                return "warning"
            case "pending":
            case "pending_send":
            case "sent":
                return "info"
            default:
                return "default"
        }
    }

    getStatusText(status: avy_api_pkg_async_AsyncStatus): string {
        switch (status) {
            case "created":
                return t`async-request-items.status.created`
            case "pending":
                return t`async-request-items.status.pending`
            case "pending_send":
                return t`async-request-items.status.pending-send`
            case "sent":
                return t`async-request-items.status.sent`
            case "processing":
                return t`async-request-items.status.processing`
            case "completed":
                return t`async-request-items.status.completed`
            case "failed":
                return t`async-request-items.status.failed`
            case "partial":
                return t`async-request-items.status.partial`
            default:
                return status
        }
    }

    private setAccessGroupId(accessGroupId: number) {
        this._accessGroupId = accessGroupId
    }

    private getAccessGroupId() {
        return this._accessGroupId
    }

    async query(advanceQuery: IAdvanceQueryModel) {
        await this.asyncRequestItems.loadAdvanceQuery(advanceQuery)
    }

    handleParams(
        columnHeaders: IColumn<IAsyncRequestItemListItem>[],
        params: IRouteParams[] = [],
    ): IPostInitialStateFilterItem[] {
        const filterableColumns = columnHeaders.filter(
            (column) =>
                column.filterable !== false && column.type !== undefined,
        )

        return params.flatMap((param) => {
            const column = filterableColumns.find(
                (col) => col.field === param.param,
            )
            if (column == null) return []

            const { field, type } = column

            try {
                if (
                    type === "dateTime" &&
                    (field === "created_at" || field === "processed_at")
                ) {
                    return handleDateFilter(param.value, field)
                }

                const value = parseFieldValue(field, param.value)
                const operator = getOperatorForField(field, type, value)

                return [
                    {
                        field: param.param,
                        operator,
                        value,
                    },
                ]
            } catch (error) {
                return []
            }
        })
    }

    dispose() {
        this.repositoryUpdatesListenerDisposer?.()
    }
}
